import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui/Button';
import { useToast } from '../components/ui/Toast';
import { useApi } from '../hooks/useApi';
import { StatusBadge } from './ui/StatusBadge';
import type { AthenaInitiative, PreviewEnvironment, PreviewEnvironmentStatus } from '@pantheon/shared';

const previewStatus: Record<PreviewEnvironmentStatus, { text: string }> = {
  provisioning: { text: 'Provisioning' },
  live: { text: 'Live' },
  failed: { text: 'Failed' },
  torn_down: { text: 'Torn down' },
};

/**
 * Preview environment — the ephemeral S3+CloudFront preview for an Athena plan, on the
 * initiative page (sibling to Argus). Shows the current deploy status + URL and lets a
 * human redeploy. Self-fetches the initiative and polls while a deploy is provisioning so
 * a provisioning→live transition shows up without a manual reload.
 */
export function PreviewEnvironmentPanel({
  initiativeId,
  preview,
  onChange,
}: {
  initiativeId: string;
  preview: PreviewEnvironment;
  /** Bubble the freshly-fetched initiative up so the page reflects status changes. */
  onChange?: (initiative: AthenaInitiative) => void;
}) {
  const { get, post } = useApi();
  const { toast } = useToast();
  const [env, setEnv] = useState<PreviewEnvironment>(preview);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const getRef = useRef(get);
  getRef.current = get;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Keep in sync when the parent re-fetches the initiative (its own poll loop).
  useEffect(() => { setEnv(preview); }, [preview]);

  const fetchPreview = useCallback(async () => {
    const result = await getRef.current<AthenaInitiative>(`/athena/initiatives/${initiativeId}`);
    if (result.success && result.data) {
      if (result.data.previewEnvironment) setEnv(result.data.previewEnvironment);
      onChangeRef.current?.(result.data);
    }
  }, [initiativeId]);

  // Poll while provisioning so the pill flips to live/failed on its own.
  useEffect(() => {
    if (env.status !== 'provisioning') return;
    const interval = setInterval(fetchPreview, 10_000);
    return () => clearInterval(interval);
  }, [env.status, fetchPreview]);

  const redeploy = async () => {
    setBusy(true);
    const result = await post<PreviewEnvironment>(`/athena/initiatives/${initiativeId}/preview/redeploy`, {});
    setBusy(false);
    if (result.success) {
      if (result.data) setEnv(result.data);
      toast('Redeploying the preview environment…', 'info');
      fetchPreview();
    } else {
      toast(result.error?.message ?? 'Failed to redeploy preview', 'error');
    }
  };

  const copyUrl = async () => {
    if (!env.url) return;
    try {
      await navigator.clipboard.writeText(env.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast('Could not copy — copy it from the link instead', 'error');
    }
  };

  const st = previewStatus[env.status] ?? { text: env.status };
  const provisioning = env.status === 'provisioning';

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-5">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Preview environment</h3>
        <Button size="sm" onClick={redeploy} loading={busy} disabled={provisioning}>
          Redeploy
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <StatusBadge status={env.status} label={st.text} />
        {env.deployedAt && (
          <span className="text-xs text-gray-500">deployed {new Date(env.deployedAt).toLocaleString()}</span>
        )}
      </div>

      {env.url && (
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <a
            className="text-sm text-primary underline hover:no-underline break-all"
            href={env.url}
            target="_blank"
            rel="noreferrer"
          >
            {env.url}
          </a>
          <button
            type="button"
            onClick={copyUrl}
            className="text-xs text-gray-500 hover:text-gray-700 underline cursor-pointer"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}

      {env.status === 'provisioning' && (
        <p className="text-xs text-gray-500 mt-2">
          Building and syncing the plan to its preview URL — this updates automatically when it goes live.
        </p>
      )}

      {env.status === 'failed' && env.error && (
        <div className="mt-2 rounded-lg border border-red-200 bg-danger-light px-3 py-2 text-xs text-danger">
          {env.error}
        </div>
      )}

      {env.status === 'torn_down' && (
        <p className="text-xs text-gray-500 mt-2">
          This preview has been torn down. Redeploy to spin it back up.
        </p>
      )}
    </div>
  );
}
